import { Dialog, Menu, Transition } from "@headlessui/react";
import { ChevronUpDownIcon } from "@heroicons/react/24/solid";
import { Fragment, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { insertTimestampIntoText } from "../../helpers/getTimestamp";
import { intervalTypeMap } from "../../helpers/intervalTypesMap";
import {
    findClientTeamForServiceDepartment,
    getDepartmentId,
    getDepartmentName,
    getTeamId,
    getTeamName,
} from "../../helpers/serviceTeamAssignment";
import LoadingOverlay from "../LoadingOverlay";
import { addSubscription } from "../../services/addSubscription";
import { getLoggedInEmployee } from "../../services/getLoggedInEmployee";
import ButtonAddTimestamp from "../../utils/ButtonAddTimestamp";
import InputTextAreaModal from "../clientServiceSubscriptionTask/updateClientServiceSubscriptionTaskModal/InputTextAreaModal";
import ServiceListBox from "../services/ServiceListBox";
import { getClientServiceMessage } from "../../helpers/clientServiceMessage";

function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
}

const getInitialFormState = () => ({
    service_id: null,
    serviceYear: new Date().getFullYear() - 1,
    end: "",
    interval: "",
    temporaryNotes: "",
    team: null,
});

export default function AddSubscriptionModal({
    open,
    setOpen,
    services,
    refreshSubs,
    client,
    onSubscriptionCreated,
    onManageTeams,
}) {
    const [teamHelperMessage, setTeamHelperMessage] = useState("");
    //const [employees, setEmployees] = useState([]);
    const [loggedInEmployee, setLoggedInEmployee] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showTemporaryNotes, setShowTemporaryNotes] = useState(false);
    const temporaryNotesRef = useRef(null);
    const [form, setForm] = useState(getInitialFormState);

    const isInvalid = (field) => !form[field] || form[field] === "";
    const shouldShowTeamErrorInsideField = form.service_id && !form.team;

    useEffect(() => {
        const getNewLoggedInEmployee = async () => {
            const currentEmployee = await getLoggedInEmployee();
            if (currentEmployee.success) {
                setLoggedInEmployee(currentEmployee.data.employeeResponse);
            }
        };
        getNewLoggedInEmployee();
    }, []);

    useEffect(() => {
        if (!open) return;

        setForm(getInitialFormState());
        setTeamHelperMessage("");
        setShowTemporaryNotes(false);
    }, [open]);

    const handleClose = () => {
        if (isSubmitting) return;
        setOpen(false);
    };

    const handleManageTeamsClick = () => {
        if (isSubmitting) return;
        onManageTeams?.();
    };

    async function handleSubmit() {
        if (isSubmitting) return;

        const requiredFields = [
            { name: "service_id", label: "Service" },
            { name: "end", label: "Due date" },
            { name: "interval", label: "Interval" },
        ];

        for (let field of requiredFields) {
            if (!form[field.name]) {
                toast.error(`${field.label} is required.`);
                return;
            }
        }

        const teamId = getTeamId(form.team);

        if (!teamId) {
            toast.error(
                "This client does not have a team assigned for the selected service department. Please assign the correct department team to the client before creating this service.",
            );
            return;
        }

        const intervalToSend = form.interval;

        const data = {
            client_id: client.id,
            interval: intervalToSend,
            serviceYear: form.serviceYear,
            start: new Date().toISOString().split("T")[0] + "T00:00:00",
            end: form.end + "T00:00:00",
            service_id: form.service_id,
            notes: "",
            temporaryNotes: form.temporaryNotes || "",
            assignedById: loggedInEmployee.employeeId,
            lastModifiedById: loggedInEmployee.employeeId,
            assignedEmployee: form.assignedEmployee,
            teamId,
        };

        setIsSubmitting(true);

        try {
            const res = await addSubscription(data);

            if (res?.data?.success) {
                toast.success("Service successfully added.");
                setOpen(false);
                const refreshedSubscriptions = await refreshSubs();
                await onSubscriptionCreated?.({
                    responseData: res.data,
                    requestData: data,
                    refreshedSubscriptions,
                });
                return;
            }

            toast.error(
                getClientServiceMessage(
                    res?.data?.message,
                    "Failed to create service.",
                ),
            );
        } catch (error) {
            toast.error(
                getClientServiceMessage(error?.message, "Failed to create service."),
            );
        } finally {
            setIsSubmitting(false);
        }
    }

    /*  const onHandleChangeAssignEmployee = (newAssignedEmployee) => {
        setForm({ ...form, assignedEmployee: newAssignedEmployee });
    }; */

    const onChangeService = (service) => {
        const selectedService =
            services.find((item) => item.id === service?.id) || service;
        const departmentId = getDepartmentId(selectedService);
        const departmentName = getDepartmentName(selectedService);
        const matchedTeam = findClientTeamForServiceDepartment(
            client?.teams,
            departmentId,
        );

        setForm((prev) => ({
            ...prev,
            service_id: service.id || service.Id,
            interval: "",
            team: matchedTeam,
        }));

        if (!departmentId) {
            setTeamHelperMessage(
                "This service does not have a department. Please update the service template before creating this service.",
            );
        } else if (!matchedTeam) {
            setTeamHelperMessage(
                `This client does not have a team assigned for the ${departmentName} department. Please assign a ${departmentName} team to the client before creating this service.`,
            );
        } else {
            setTeamHelperMessage("");
        }
    };

    const onDueDateChange = (e) => {
        setForm({ ...form, end: e.target.value });
    };

    const onHandleChange = (e) => {
        const { name, value } = e.target;
        setForm({ ...form, [name]: value });
    };

    const onHandleClickAddTimestamp = (fieldName, textareaRef) => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const current = form[fieldName] || "";

        if (document.activeElement !== textarea) {
            textarea.focus();
            const endPos = current.length;
            textarea.setSelectionRange(endPos, endPos);
        }

        const start =
            typeof textarea.selectionStart === "number"
                ? textarea.selectionStart
                : current.length;
        const end =
            typeof textarea.selectionEnd === "number"
                ? textarea.selectionEnd
                : start;

        const { caretPosition, value: updated } = insertTimestampIntoText(
            current,
            start,
            end,
            loggedInEmployee,
        );

        setForm((prev) => ({
            ...prev,
            [fieldName]: updated,
        }));

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                textarea.focus();
                textarea.setSelectionRange(caretPosition, caretPosition);
            });
        });
    };

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={handleClose}>
                <LoadingOverlay
                    isLoading={isSubmitting}
                    message="Creating service, please wait..."
                />
                <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex items-end justify-center min-h-full p-4 text-center sm:items-center sm:p-0">
                        <Dialog.Panel className="relative px-4 pt-5 pb-4 overflow-hidden text-left transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:max-w-sm sm:p-6">
                            <div>
                                <Dialog.Title
                                    as="h3"
                                    className="text-lg font-medium leading-6 text-gray-900"
                                >
                                    New service for: {client?.clientName}
                                </Dialog.Title>

                                <ServiceListBox
                                    services={services}
                                    form={form}
                                    setForm={setForm}
                                    onChangeService={onChangeService}
                                    isError={isInvalid("service_id")}
                                    disabled={isSubmitting}
                                />

                                <div className="mt-4">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Interval{" "}
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <Menu
                                        as="div"
                                        className="relative inline-block w-full text-left"
                                    >
                                        <Menu.Button
                                            className={classNames(
                                                "inline-flex justify-between w-full py-2 pl-3 pr-2 text-sm font-normal border rounded-md shadow-sm",
                                                isSubmitting
                                                    ? "cursor-not-allowed bg-gray-100 text-gray-400"
                                                    : "bg-white text-gray-700",
                                                isInvalid("interval")
                                                    ? "border-red-500"
                                                    : "border-gray-200",
                                            )}
                                            disabled={isSubmitting}
                                        >
                                            {form.interval || "Select interval"}
                                            <ChevronUpDownIcon className="w-4 h-4 mt-0.5" />
                                        </Menu.Button>
                                        <Transition
                                            as={Fragment}
                                            enter="transition ease-out duration-100"
                                            enterFrom="transform opacity-0 scale-95"
                                            enterTo="transform opacity-100 scale-100"
                                            leave="transition ease-in duration-75"
                                            leaveFrom="transform opacity-100 scale-100"
                                            leaveTo="transform opacity-0 scale-95"
                                        >
                                            <Menu.Items className="right-0 z-10 w-full mt-2 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                                <div className="py-1">
                                                    {intervalTypeMap.map(
                                                        (interval) => (
                                                            <Menu.Item
                                                                key={interval.name}
                                                            >
                                                                {({ active }) => (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() =>
                                                                            setForm({
                                                                                ...form,
                                                                                interval:
                                                                                    interval.name,
                                                                            })
                                                                        }
                                                                        className={classNames(
                                                                            active
                                                                                ? "bg-gray-100 text-gray-900"
                                                                                : "text-gray-700",
                                                                            "block px-4 py-2 text-sm cursor-pointer w-full text-left",
                                                                        )}
                                                                    >
                                                                        {
                                                                            interval.name
                                                                        }
                                                                    </button>
                                                                )}
                                                            </Menu.Item>
                                                        ),
                                                    )}
                                                </div>
                                            </Menu.Items>
                                        </Transition>
                                    </Menu>
                                </div>

                                <div className="mt-4">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Year
                                    </label>
                                    <input
                                        type="number"
                                        min="2000"
                                        max="2100"
                                        value={form.serviceYear}
                                        disabled={isSubmitting}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                serviceYear: e.target.value,
                                            })
                                        }
                                        className={classNames(
                                            "block w-full px-2 py-1 mt-1 border border-gray-300 rounded-md shadow-sm",
                                            isSubmitting
                                                ? "cursor-not-allowed bg-gray-100 text-gray-400"
                                                : "bg-white text-gray-900",
                                        )}
                                    />
                                </div>

                                {/*  <AssignedByListBox
                                    label="Assigned to"
                                    employees={employees}
                                    assignedBy={form.assignedEmployee}
                                    onChange={onHandleChangeAssignEmployee}
                                    isError={isInvalid("assignedEmployee")}
                                /> */}

                                {/*  <AssignedByListBox
                                    label="Assigned to Manager"
                                    employees={managers || []}
                                    assignedBy={form.assignedEmployee}
                                    onChange={onHandleChangeAssignEmployee}
                                    isError={isInvalid("assignedEmployee")}
                                /> */}

                                <div className="mt-4">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Team <span className="text-red-500">*</span>
                                    </label>
                                    <div
                                        className={classNames(
                                            "mt-1 rounded-md border px-3 py-2 text-sm",
                                            form.team
                                                ? "border-gray-300 bg-gray-100 text-gray-700"
                                                : form.service_id
                                                  ? "border-red-300 bg-red-50 text-red-600"
                                                  : "border-gray-300 bg-gray-100 text-slate-500",
                                        )}
                                    >
                                        {form.team ? (
                                            <div>
                                                <div className="font-medium text-slate-800">
                                                    {getTeamName(form.team)}
                                                </div>
                                                <div className="text-xs text-slate-500">
                                                    Automatically assigned from the
                                                    client's{" "}
                                                    {getDepartmentName(form.team)}{" "}
                                                    department team.
                                                </div>
                                            </div>
                                        ) : form.service_id ? (
                                            <div>
                                                <span>
                                                    {teamHelperMessage ||
                                                        "This client does not have a matching team for the selected service department."}
                                                </span>

                                                {onManageTeams ? (
                                                    <button
                                                        type="button"
                                                        onClick={handleManageTeamsClick}
                                                        disabled={isSubmitting}
                                                        className="mt-3 inline-flex items-center justify-center rounded-md bg-teal-500 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-teal-600 disabled:cursor-not-allowed disabled:bg-teal-300"
                                                    >
                                                        Edit client teams
                                                    </button>
                                                ) : null}
                                            </div>
                                        ) : (
                                            <span>
                                                Select a service to auto-assign the
                                                client team.
                                            </span>
                                        )}
                                    </div>

                                    {teamHelperMessage &&
                                    !shouldShowTeamErrorInsideField ? (
                                        <p className="mt-1 text-xs text-red-600">
                                            {teamHelperMessage}
                                        </p>
                                    ) : null}
                                </div>

                                <div className="mt-4">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Due Date{" "}
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        value={form.end}
                                        disabled={isSubmitting}
                                        onChange={onDueDateChange}
                                        className={classNames(
                                            "mt-1 block w-full rounded-md border px-2 py-1 shadow-sm",
                                            isSubmitting
                                                ? "cursor-not-allowed bg-gray-100 text-gray-400"
                                                : "bg-white text-gray-900",
                                            isInvalid("end")
                                                ? "border-red-500"
                                                : "border-gray-300",
                                        )}
                                    />
                                </div>

                                <div className="mt-4">
                                    <div>
                                        {!showTemporaryNotes ? (
                                            <button
                                                className="inline-flex items-center p-1 mt-2 text-base text-white bg-teal-500 border border-transparent rounded-sm shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                                                onClick={() =>
                                                    setShowTemporaryNotes(true)
                                                }
                                                type="button"
                                                disabled={isSubmitting}
                                            >
                                                Add notes
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() =>
                                                    setShowTemporaryNotes(false)
                                                }
                                                type="button"
                                                disabled={isSubmitting}
                                                className="inline-flex items-center p-1 mt-2 text-base text-white bg-red-500 border border-transparent rounded-sm shadow-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                            >
                                                Hide notes
                                            </button>
                                        )}

                                        {showTemporaryNotes && (
                                            <>
                                                <InputTextAreaModal
                                                    ref={temporaryNotesRef}
                                                    label="Notes"
                                                    name="temporaryNotes"
                                                    onChange={onHandleChange}
                                                    value={form.temporaryNotes || ""}
                                                    disabled={isSubmitting}
                                                    placeholder="Notes"
                                                />
                                                <ButtonAddTimestamp
                                                    onHandleClick={() =>
                                                        onHandleClickAddTimestamp(
                                                            "temporaryNotes",
                                                            temporaryNotesRef,
                                                        )
                                                    }
                                                />
                                            </>
                                        )}
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                    className={classNames(
                                        "w-full px-4 py-2 mt-6 text-white rounded-md",
                                        isSubmitting
                                            ? "cursor-not-allowed bg-teal-300"
                                            : "bg-teal-500 hover:bg-teal-600",
                                    )}
                                >
                                    {isSubmitting ? "Saving..." : "Save service"}
                                </button>
                            </div>
                        </Dialog.Panel>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
